import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ConversionRateBySourceDto {
  month: string;
  source: string;
  enquiries: number;
  conversions: number;
  conversionPercentage: number;
}

export interface EnquiryTrendPointDto {
  date: string;
  source: string;
  count: number;
}

export interface TimeToConversionDto {
  source: string;
  averageDaysToConvert: number;
  sampleSize: number;
}

export interface UnconvertedLeadDto {
  phoneNumber: string;
  name?: string;
  source: string;
  firstSeenAt: string;
  daysSinceFirstSeen: number;
}

export interface CapiDeliveryHealthDto {
  sent: number;
  pendingOrFailed: number;
}

export interface AdPerformanceDto {
  adId: string;
  adName?: string;
  campaignName?: string;
  adsetName?: string;
  spend: number;
  leads: number;
  signedUp: number;
  pickupsBooked: number;
  pickupsCompleted: number;
  costPerLead?: number;
  costPerSignUp?: number;
  costPerBooking?: number;
  costPerAcquisition?: number;
  companyRevenue: number;
  roas?: number;
}

export interface CampaignOutreachResultDto {
  /** "NoSignupWinBack" | "NoBookingWinBack" | "GeoZoneLaunch" | "Total" */
  campaign: string;
  messagesSent: number;
  uniqueRecipients: number;
  signedUp: number;
  pickupsBooked: number;
  signedUpOrBooked: number;
  conversionPercentage: number;
}

export interface CampaignOutreachSummaryDto {
  campaigns: CampaignOutreachResultDto[];
  total: CampaignOutreachResultDto;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface AdPerformancePaginatedResponse extends PaginatedResponse<AdPerformanceDto> {
  directTraffic: AdPerformanceDto;
}

export const DIRECT_TRAFFIC_AD_ID = 'direct-traffic';

@Injectable({
  providedIn: 'root'
})
export class MarketingAttributionService {
  private readonly baseUrl = `${environment.apiConfig.uri}/api/MarketingAttribution`;

  constructor(private http: HttpClient) { }

  getConversionRateBySource(from?: string, to?: string): Observable<ConversionRateBySourceDto[]> {
    const params = this.buildDateRangeParams(from, to);
    return this.http.get<ConversionRateBySourceDto[]>(`${this.baseUrl}/conversion-rate-by-source`, { params });
  }

  getEnquiryTrend(from?: string, to?: string): Observable<EnquiryTrendPointDto[]> {
    const params = this.buildDateRangeParams(from, to);
    return this.http.get<EnquiryTrendPointDto[]>(`${this.baseUrl}/enquiry-trend`, { params });
  }

  getTimeToConversion(from?: string, to?: string): Observable<TimeToConversionDto[]> {
    const params = this.buildDateRangeParams(from, to);
    return this.http.get<TimeToConversionDto[]>(`${this.baseUrl}/time-to-conversion`, { params });
  }

  getUnconvertedLeads(
    source?: string,
    from?: string,
    to?: string,
    pageIndex = 0,
    pageSize = 10
  ): Observable<PaginatedResponse<UnconvertedLeadDto>> {
    const params = this.buildDateRangeParams(from, to, source ? { source } : undefined)
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);
    return this.http.get<PaginatedResponse<UnconvertedLeadDto>>(`${this.baseUrl}/unconverted-leads`, { params });
  }

  getCapiDeliveryHealth(from?: string, to?: string): Observable<CapiDeliveryHealthDto> {
    const params = this.buildDateRangeParams(from, to);
    return this.http.get<CapiDeliveryHealthDto>(`${this.baseUrl}/capi-delivery-health`, { params });
  }

  getAdPerformance(
    from?: string,
    to?: string,
    search?: string,
    pageIndex = 0,
    pageSize = 10
  ): Observable<AdPerformancePaginatedResponse> {
    const params = this.buildDateRangeParams(from, to, search ? { search } : undefined)
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);
    return this.http.get<AdPerformancePaginatedResponse>(`${this.baseUrl}/ad-performance`, { params });
  }

  /** How many people messaged by the WhatsApp campaigns signed up / booked afterwards; from/to filter by send date. */
  getCampaignOutreachResults(from?: string, to?: string): Observable<CampaignOutreachSummaryDto> {
    const params = this.buildDateRangeParams(from, to);
    return this.http.get<CampaignOutreachSummaryDto>(`${this.baseUrl}/campaign-outreach-results`, { params });
  }

  private buildDateRangeParams(from?: string, to?: string, extra?: Record<string, string>): HttpParams {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', from);
    }
    if (to) {
      params = params.set('to', to);
    }
    if (extra) {
      for (const key of Object.keys(extra)) {
        params = params.set(key, extra[key]);
      }
    }
    return params;
  }
}
